import useToast from "./useToast";
import useRegistrationModal from "@/composables/useRegistrationModal";
import { computed } from "vue";
import { useRouter } from "vue-router";

import useGoogleTag from "@/composables/useGoogleTag";

const useAuth = (
  store,
  ethereumService,
  cardanoService,
  router = useRouter()
) => {
  const { showToast } = useToast();
  const { registerEvent, registerException } = useGoogleTag();
  const disconnect = async (disconnectProvider = false) => {
    await store.dispatch("contracts/resetState");
    await store.dispatch("auth/disconnect");
    if (disconnectProvider) {
      await ethereumService.disconnectProvider();
    }
    return await router.push("/");
  };

  const disconnectInRouter = async () => {
    await store.dispatch("contracts/resetState");
    await store.dispatch("auth/disconnect");
    await ethereumService.disconnectProvider();
  };

  const handleAccountChange = async (account) => {
    await store.dispatch("contracts/fetchIsValidNetwork");
    await showToast(
      `Account changed: ${account}`,
      `Please, sign the transaction on Nami Wallet to continue.`,
      "Success"
    );
    // await disconnect();
    await loginOrSignUpCardano(true);
  };

  const handleChainChange = async (chain) => {
    await store.dispatch("contracts/fetchIsValidNetwork");
    if (chain === "Selected network is not supported.") {
      await showToast("Network not supported", chain, "Error");
      await store.dispatch("minting/commitByKey", {
        isBeginConnectionModalOpen: true,
      });
      return await disconnect(true);
    }
    await showToast("Network changed", chain, "Success");
    await store.dispatch("contracts/fetchNetwork");
    await store.dispatch("contracts/fetchBalance");
  };

  const loginOrSignUpCardano = async (isAccountChange = false) => {
    registerEvent("Connect to a wallet button");
    await cardanoService.connectToProvider();

    console.log("loginOrSignUpCardano", isAccountChange);
    const namiWallet = await cardanoService.namiWallet();
    if (await namiWallet.isEnabled()) {
      console.log("Nami Wallet Address:", cardanoService.paymentAddr);

      const isValidNetwork = await cardanoService.isValidNetwork();
      await store.dispatch("contracts/fetchIsValidNetwork");
      if (!isValidNetwork) {
        registerException("Network not supported", false);
        await showToast(
          "Network not supported",
          "Selected network is not supported.",
          "Error"
        );
        return await disconnect(true);
      }
      if (!isAccountChange) {
        await cardanoService.onAccountChanged(handleAccountChange);
      }
      await cardanoService.onChainChanged(handleChainChange);
      await store.dispatch("users/updatePendingTransaction", true);
      // await store.dispatch("contracts/fetchNetwork");
      let publicAddress = await store.dispatch("contracts/fetchAddressCardano");
      await store.dispatch("contracts/fetchBalance");
      let nonce = await store.dispatch("auth/fetchNonce", publicAddress);
      if (!nonce) {
        const signupResponse = await store.dispatch(
          "auth/signUp",
          publicAddress
        );
        nonce = signupResponse.data.nonce;
      }
      await store.dispatch("minting/commitByKey", {
        isBeginConnectionModalOpen: false,
      });
      await cardanoService.signNonce(nonce);
      const signature = cardanoService.signedNonce;
      if (signature === "rejected") {
        registerException("Metamask signature rejected", false);
        await showToast(
          "Nami error",
          "Please, sign the message to access the advanced features of the application.",
          "Error"
        );
        await store.dispatch("users/updatePendingTransaction", false);
        await store.dispatch("minting/commitByKey", {
          isBeginConnectionModalOpen: true,
        });
        return disconnect();
      }
      await store.dispatch("auth/verifyNonce", {
        publicAddress,
        signature,
      });
      await store.dispatch("userProfile/fetchMe");
      const { toggleRegistrationFlag } = useRegistrationModal(store);
      await store.dispatch("users/updatePendingTransaction", false);
      await store.dispatch("userProfile/fetchMe");
      const registered = await isUserRegistered();
      await store.dispatch("contracts/fetchNetworkName");
      if (!registered) {
        toggleRegistrationFlag();
      }
      if (registered) {
        await router.push("/");
      }

      console.log("Successful login");
      registerEvent("Successful login");
      await router.push("/");
    } else {
      await store.dispatch("users/updatePendingTransaction", false);
      await store.dispatch("minting/commitByKey", {
        isBeginConnectionModalOpen: true,
      });
      console.log("Rejected");
      return disconnect();
    }
  };

  const loginOrSignUp = async (isAccountChange = false) => {
    registerEvent("Connect to a wallet button");

    await ethereumService.connectToProvider();

    const isValidNetwork = await ethereumService.isValidNetwork();
    await store.dispatch("contracts/fetchIsValidNetwork");
    if (!isValidNetwork) {
      registerException("Network not supported", false);
      await showToast(
        "Network not supported",
        "Selected network is not supported.",
        "Error"
      );
      return await disconnect(true);
    }
    if (!isAccountChange) {
      await ethereumService.onAccountChanged(handleAccountChange);
    }
    await ethereumService.onChainChanged(handleChainChange);
    await store.dispatch("users/updatePendingTransaction", true);
    await store.dispatch("contracts/fetchNetwork");
    let publicAddress = await store.dispatch("contracts/fetchAddress");
    await store.dispatch("contracts/fetchBalance", publicAddress);
    let nonce = await store.dispatch("auth/fetchNonce", publicAddress);
    if (!nonce) {
      const signupResponse = await store.dispatch("auth/signUp", publicAddress);
      nonce = signupResponse.data.nonce;
    }
    await store.dispatch("minting/commitByKey", {
      isBeginConnectionModalOpen: false,
    });
    const signature = await store.dispatch("contracts/signNonce", nonce);
    if (signature === "rejected") {
      registerException("Metamask signature rejected", false);
      await showToast(
        "MetaMask error",
        "Please, sign the message to access the advanced features of the application.",
        "Error"
      );
      await store.dispatch("users/updatePendingTransaction", false);
      await store.dispatch("minting/commitByKey", {
        isBeginConnectionModalOpen: true,
      });
      return disconnect();
    }
    await store.dispatch("auth/verifyNonce", {
      signature,
      publicAddress,
    });
    await store.dispatch("userProfile/fetchMe");
    const { toggleRegistrationFlag } = useRegistrationModal(store);
    await store.dispatch("users/updatePendingTransaction", false);
    await store.dispatch("userProfile/fetchMe");
    const registered = await isUserRegistered();
    if ((await ethereumService.isProviderConnected()) && !registered) {
      toggleRegistrationFlag();
    }
    await store.dispatch("contracts/fetchNetworkName");
    if (registered) {
      await router.push("/");
    }
    registerEvent("Successful login");
  };

  const openRegistrationModal = async () => {
    const address = computed(
      () => store.getters["contracts/getAddressCardano"]
    );
    const { toggleRegistrationFlag } = useRegistrationModal(store);
    if (address.value) {
      const registered = await isUserRegistered();
      if (!registered) {
        toggleRegistrationFlag();
      }
    }
  };

  const isUserRegistered = async () => {
    await store.dispatch("auth/fetchMe");
    const me = store.getters["auth/getRegisteredUser"];
    return Boolean(me.displayName);
  };

  return {
    loginOrSignUp,
    loginOrSignUpCardano,
    handleChainChange,
    handleAccountChange,
    openRegistrationModal,
    disconnectInRouter,
  };
};

export default useAuth;
